import React, { useCallback, useEffect, useState } from 'react';
import { Col, DatePicker, Form, Input, Modal, Row, Switch, message } from 'antd';
import {
  useForgotPasswordMutation,
  useGetMyAccountQuery,
  useUpdateProfileMutation,
  useUpdateSettingMutation,
  UpdateProfileInput,
  Address,
} from 'graphql/types';
import { Button, Avatar, ImageInput } from 'components/elements';
import { dateFormat } from 'settings/configs';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { ResizeImageResult } from 'helpers';
import {
  MetaRow,
  MetaText,
  ProfileActionWrap,
  ProfileHeader,
  ProfileHeading,
  ProfileInfoWrap,
  ProfileName,
  ProfileTag,
  ProfileWrapper,
  ProfileBody,
  ProfileTop,
  SettingsCard,
  SettingsLabel,
  SettingsRow,
  SettingsTopGrid,
  SettingsTopItem,
  SettingsValueWrap,
  UserIdText,
  EditProfileModalBody,
  EditProfileAvatarWrap,
} from './profile.styled';
import { CrownOutlined, EditOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';

interface FormFields {
  firstname: string;
  lastname: string;
  username: string;
  email: string;
  description: string;
  avatar: ResizeImageResult;
  address: Address;
  birthdate: moment.Moment;
}

const capitalize = (value?: string) => {
  if (!value) return '';
  return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
};

type EditProfileAvatarFieldProps = {
  value?: ResizeImageResult;
  onChange?: (value: ResizeImageResult) => void;
  fallbackUrl?: string;
  loading?: boolean;
};

function EditProfileAvatarField({ value, onChange, fallbackUrl, loading }: EditProfileAvatarFieldProps) {
  const isNewSelection = Boolean(value?.url?.startsWith('data:'));
  const previewUrl = isNewSelection ? value!.url : fallbackUrl ?? value?.url;

  const handleChange = (next: ResizeImageResult) => {
    onChange?.(next);
  };

  return (
    <EditProfileAvatarWrap>
      <ImageInput width={180} height={180} cropping compact onChange={handleChange}>
        <Avatar avatarUrl={previewUrl} size="lg" editable loading={loading} />
      </ImageInput>
    </EditProfileAvatarWrap>
  );
}

export default function MyProfile() {
  const history = useHistory();
  const { data: accountResult } = useGetMyAccountQuery();
  const [updateProfile, { loading: savingProfile }] = useUpdateProfileMutation();
  const [updateSetting, { loading: savingSetting }] = useUpdateSettingMutation();
  const [forgotPassword, { loading: requestingPassword }] = useForgotPasswordMutation();
  const [form] = Form.useForm<FormFields>();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [receiveEmailEnabled, setReceiveEmailEnabled] = useState(false);

  const accountAttr = accountResult?.myAccount?.data?.attributes;
  const avatarValue = Form.useWatch('avatar', form);
  const fullName = `${capitalize(accountAttr?.firstname)} ${capitalize(accountAttr?.lastname)}`.trim();
  const location = [accountAttr?.address?.data?.attributes?.city, accountAttr?.address?.data?.attributes?.country]
    .filter(Boolean)
    .join(', ');
  const currentPlan =
    accountAttr?.currentSubscription?.data?.attributes?.plan?.data?.attributes?.name || 'Friends';

  const syncForm = useCallback(() => {
    if (!accountAttr) return;
    const { __typename, ...restAddress } = accountAttr?.address?.data?.attributes || {};
    form.setFieldsValue({
      firstname: accountAttr.firstname || '',
      lastname: accountAttr.lastname || '',
      username: accountAttr.username || '',
      email: accountAttr.email || '',
      description: accountAttr.description || '',
      avatar: { blob: null, url: accountAttr.avatarUrl },
      address: isEmpty(restAddress) ? null : restAddress,
      birthdate: accountAttr.birthdate ? moment(accountAttr.birthdate) : null,
    } as any);
    setNotificationEnabled(Boolean((accountAttr as any)?.notification));
    setReceiveEmailEnabled(Boolean((accountAttr as any)?.receiveEmail));
  }, [accountAttr, form]);

  useEffect(() => {
    syncForm();
  }, [syncForm]);

  const onSaveProfile = async (values: FormFields) => {
    const { avatar, address: _address, birthdate, ...rawValues } = values;
    const input: UpdateProfileInput = {
      ...rawValues,
      ...(birthdate ? { birthdate: birthdate.toISOString() } : {}),
    };
    if (avatar?.blob) {
      input.avatar = avatar.blob;
    }
    try {
      await updateProfile({ variables: { input } });
      setIsEditDialogOpen(false);
      message.success('Profile updated');
    } catch (err) {
      if (err instanceof Error) {
        message.error(err.message);
      }
      throw err;
    }
  };

  const handleSaveClick = () => form.validateFields().then((values) => onSaveProfile(values));

  const saveSetting = async (field: 'notification' | 'receiveEmail', value: boolean) => {
    try {
      await updateSetting({ variables: { input: { field, value: value ? 1 : 0 } } });
    } catch (err) {
      if (err instanceof Error) {
        message.error(err.message);
      }
    }
  };

  const onRequestPassword = async () => {
    try {
      await forgotPassword({ variables: { email: accountAttr?.email } });
      setCodeSent(true);
      message.success('Password reset request sent');
    } catch (err) {
      if (err instanceof Error) {
        message.error(err.message);
      }
    }
  };

  const openEditDialog = () => {
    setIsEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setIsEditDialogOpen(false);
  };

  useEffect(() => {
    if (isEditDialogOpen && accountAttr) {
      syncForm();
    }
  }, [isEditDialogOpen, accountAttr, syncForm]);

  return (
    <ProfileWrapper>
      <ProfileTop>
          <ProfileHeader>
            <Avatar avatarUrl={accountAttr?.avatarUrl} size="lg" />
            <ProfileInfoWrap>
              <ProfileHeading>
                <ProfileName>{fullName || 'Profile'}</ProfileName>
                <ProfileTag>{currentPlan}</ProfileTag>
              </ProfileHeading>
              <UserIdText>Username: {accountAttr?.username || '—'}</UserIdText>
              <UserIdText>Email: {accountAttr?.email || '—'}</UserIdText>
              <MetaRow>
                <MetaText>
                  <i className="icon-pin" /> Location: {location || '—'}
                </MetaText>
                <MetaText>
                  <i className="icon-birthday" /> Birthday:{' '}
                  {accountAttr?.birthdate ? moment(accountAttr.birthdate).format(dateFormat) : '—'}
                </MetaText>
                <MetaText>
                  <i className="icon-calendar2" /> Joined:{' '}
                  {accountAttr?.createdAt ? moment(accountAttr.createdAt).format(dateFormat) : '—'}
                </MetaText>
              </MetaRow>
            </ProfileInfoWrap>
            <ProfileActionWrap>
              <Button type="primary" onClick={openEditDialog}>
                <EditOutlined />
                Edit Profile
              </Button>
              <Button type="default" onClick={() => history.push('/account/membership')}>
                <CrownOutlined />
                Upgrade supporter level
              </Button>
            </ProfileActionWrap>
          </ProfileHeader>
        </ProfileTop>

        <ProfileBody>
          <SettingsCard>
            <h3>Settings</h3>
            <SettingsTopGrid>
              <SettingsTopItem>
                <SettingsLabel>Notification: </SettingsLabel>
                <SettingsValueWrap>
                  <Switch
                    checked={notificationEnabled}
                    loading={savingSetting}
                    onChange={(checked) => {
                      setNotificationEnabled(checked);
                      saveSetting('notification', checked);
                    }}
                  />
                </SettingsValueWrap>
              </SettingsTopItem>
              <SettingsTopItem>
                <SettingsLabel>Receive Email: </SettingsLabel>
                <SettingsValueWrap>
                  <Switch
                    checked={receiveEmailEnabled}
                    loading={savingSetting}
                    onChange={(checked) => {
                      setReceiveEmailEnabled(checked);
                      saveSetting('receiveEmail', checked);
                    }}
                  />
                </SettingsValueWrap>
              </SettingsTopItem>
            </SettingsTopGrid>
            <SettingsRow>
              <SettingsLabel>Password</SettingsLabel>
              <SettingsValueWrap>
                <Button type="primary" onClick={onRequestPassword} loading={requestingPassword}>
                  {codeSent ? 'Request sent' : 'Request change password'}
                </Button>
              </SettingsValueWrap>
            </SettingsRow>
          </SettingsCard>
      </ProfileBody>

      <Modal
        title="Update Profile"
        open={isEditDialogOpen}
        destroyOnClose
        onCancel={closeEditDialog}
        onOk={handleSaveClick}
        okText="Save"
        cancelText="Cancel"
        confirmLoading={savingProfile}
      >
        <Form form={form} layout="vertical" requiredMark={false} onFinish={onSaveProfile}>
            <EditProfileModalBody>
              <Form.Item name="avatar" style={{ marginBottom: 16 }} getValueFromEvent={(value) => value}>
                <EditProfileAvatarField
                  value={avatarValue}
                  onChange={(next) => form.setFieldValue('avatar', next)}
                  fallbackUrl={accountAttr?.avatarUrl}
                  loading={savingProfile}
                />
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item name="firstname" label="First name">
                    <Input className="auth-input-text" placeholder="First name" autoComplete="given-name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="lastname" label="Last name">
                    <Input className="auth-input-text" placeholder="Last name" autoComplete="family-name" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item name="username" label="Username">
                    <Input className="auth-input-text" placeholder="Username" autoComplete="username" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: true, message: 'Please enter your email.' },
                      { type: 'email', message: 'The input is not a valid email.' },
                    ]}
                  >
                    <Input className="auth-input-text" placeholder="Enter your email" autoComplete="email" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item name="birthdate" label="Birthday">
                    <DatePicker className="auth-date-input" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="description" label="Location">
                <Input className="auth-input-text" placeholder="Enter your location" />
              </Form.Item>
          </EditProfileModalBody>
        </Form>
      </Modal>
    </ProfileWrapper>
  );
}